import CmEpisodicItemPicture from '../cm-episodic-item-pictures/cm-episodic-item-picture.entity';
import CmPermission from '../cm-permissions/cm-permission.entity';
import CmWrapBoxEpisodicItem from '../cm-wrap-box-episodic-items/cm-wrap-box-episodic-item.entity';
import DepartmentSetting from '../department-settings/department-setting.entity';
import { DEPARTMENTS, FIELD_TYPES, PICTURE_TYPES } from '../enums/enums';
import Episodic from '../episodics/episodic.entity';
import HaChangeFieldNames from '../ha-change-field-names/ha-change-field-names.entity';
import HaLookPicture from '../ha-look-pictures/ha-look-picture.entity';
import HaPermission from '../ha-permissions/ha-permission.entity';
import MuChangeFieldNames from '../mu-change-field-names/mu-change-field-names.entity';
import MuLookPicture from '../mu-look-pictures/mu-look-picture.entity';
import MuPermission from '../mu-permissions/mu-permission.entity';
import PrEpisodicPropPicture from '../pr-episodic-prop-pictures/pr-episodic-prop-picture.entity';
import PrPermission from '../pr-permissions/pr-permission.entity';
import PrWrapBoxEpisodicProp from '../pr-wrap-box-episodic-props/pr-wrap-box-episodic-prop.entity';
import SetPermission from '../set-permissions/set-permission.entity';
import SetPiecePicture from '../set-piece-pictures/set-piece-picture.entity';
import SetWrapBoxSetPiece from '../set-wrap-box-set-pieces/set-wrap-box-set-piece.entity';
import { Taggable } from '../taggable';
import {
	Change,
	ChangeScene,
	CmCategory,
	CmChangePicture,
	CmEpisodicActor,
	CmEpisodicCharacterNote,
	CmSceneNote,
	CmStatus,
	CmStorageLocation,
	CmWrapBox,
	EpisodicCharacterPicture,
	EpisodicItem,
	EpisodicProp,
	HaChange,
	HaChangeScene,
	HaEpisodicActor,
	HaEpisodicCharacterNote,
	HaSceneNote,
	MuChange,
	MuChangeScene,
	MuEpisodicActor,
	MuEpisodicCharacterNote,
	MuSceneNote,
	PictureScene,
	PrEpisodicActor,
	PrEpisodicCharacterNote,
	ProdBase,
	PropCategory,
	PropStatus,
	PropStorageLocation,
	PrSceneNote,
	PrWrapBox,
	Scene,
	SetPiece,
	SetPieceStatus,
	SetPieceType,
	SetStorageLocation,
	SetWrapBox,
	Base,
} from '../..';

export type AnyInventory = EpisodicItem | EpisodicProp | SetPiece;
export type AnyChange = Change | HaChange | MuChange;
export type AnyChangeLookPicture =
	| CmChangePicture
	| HaLookPicture
	| MuLookPicture;
export type AnyChangeScene = ChangeScene | HaChangeScene | MuChangeScene;
export type AnyLook = HaChange | MuChange;
export type AnyLookFieldNames = HaChangeFieldNames | MuChangeFieldNames;
export type AnyWrapBox = CmWrapBox | SetWrapBox | PrWrapBox;
export type AnyWrapBoxInventoryRelation =
	| CmWrapBoxEpisodicItem
	| PrWrapBoxEpisodicProp
	| SetWrapBoxSetPiece;
export type AnyPicture =
	| PictureScene
	| CmChangePicture
	| EpisodicCharacterPicture
	| CmEpisodicItemPicture
	| PrEpisodicPropPicture
	| SetPiecePicture
	| HaLookPicture
	| MuLookPicture;
export type InventoryPicture =
	| CmEpisodicItemPicture
	| PrEpisodicPropPicture
	| SetPiecePicture;

export type InventoryDepartment =
	| DEPARTMENTS.CM
	| DEPARTMENTS.PR
	| DEPARTMENTS.SET;
export type DepartmentPermission =
	| CmPermission
	| HaPermission
	| MuPermission
	| PrPermission
	| SetPermission;

export type Category = CmCategory | PropCategory | SetPieceType;
export type Status = CmStatus | PropStatus | SetPieceStatus;
export type StorageLocation =
	| CmStorageLocation
	| PropStorageLocation
	| SetStorageLocation;

export type SceneNote = CmSceneNote | HaSceneNote | MuSceneNote | PrSceneNote;

export type AnyEpisodicCharacterNote =
	| CmEpisodicCharacterNote
	| HaEpisodicCharacterNote
	| MuEpisodicCharacterNote
	| PrEpisodicCharacterNote;
export type EpisodicActorNote =
	| CmEpisodicActor
	| HaEpisodicActor
	| MuEpisodicActor
	| PrEpisodicActor;

export interface TagInterface {
	entity: Taggable;
	type: PICTURE_TYPES;
}

type BaseESDocType = { [fieldName: string]: string | number | number[] };

export interface BaseESDoc extends BaseESDocType {
	id: number;
}

export interface EpisodicBaseESDoc extends BaseESDoc {
	episodic_id: number;
}

export interface ProdBaseESDoc extends BaseESDoc {
	prod_id: number;
}

export interface EpisodicDepartmentInfo {
	episodic: Episodic;
	departmentSetting: DepartmentSetting;
	departmentPermission: DepartmentPermission;
}

export type SortOrder = 'asc' | 'desc';

export interface ColumnWidth {
	columnName: string;
	width: number | string;
	widthDraft?: number;
}

export interface TableColumn {
	canonical_name: string;
	column?: number;
	getCellValue?: (row: any) => string; // tslint:disable-line:no-any
	maxLength?: number;
	name: string;
	placeholder?: string;
	type: FIELD_TYPES;
	width: number | string;
	widthDraft?: number;
}

export interface DeptEntityId {
	department: DEPARTMENTS;
	id: number;
}

export interface InventorySatellitesList {
	cmCategories?: CmCategory[];
	cmStatuses?: CmStatus[];
	cmStorageLocations?: CmStorageLocation[];
	propCategories?: PropCategory[];
	propStatuses?: PropStatus[];
	propStorageLocations?: PropStorageLocation[];
	setPieceStatuses?: SetPieceStatus[];
	setPieceTypes?: SetPieceType[];
	setStorageLocations?: SetStorageLocation[];
}

export interface ChangesTableItem {
	change: {
		changeId: number;
		changeNum: string;
	};
	character: {
		characterName: string;
		episodicCharacterId: number;
	};
	episode: string;
}

export interface CreateDeleteChangeScene {
	newChangeScene?: Partial<AnyChangeScene>;
	oldChangeScene?: AnyChangeScene;
}

export interface PartialProdBase extends Partial<ProdBase> {
	prod_id: number;
}

export interface PartialScene extends Partial<Scene> {
	prod_id: number;
}

export interface Results<T = Base> {
	[className: string]: T[];
}

export interface ScriptDate {
	shoot_time: number;
	script_day: string;
	script_month: string;
	script_year: string;
}

export interface CrossDeptEpCharPictures {
	episodicCharacterPictures: EpisodicCharacterPicture[];
	changePictures: CmChangePicture[];
	haLookPictures: HaLookPicture[];
	muLookPictures: MuLookPicture[];
}

export interface WrapBoxFindOptions {
	storageLocationIds: number[];
	departments: DEPARTMENTS[];
	search: string;
	sortBy: string;
	sortOrder: string;
}

export interface DeptWrapBoxes {
	CmWrapBox?: CmWrapBox[];
	PrWrapBox?: PrWrapBox[];
	SetWrapBox?: SetWrapBox[];
}

export interface PartialDeptWrapBoxes {
	CmWrapBox?: Partial<CmWrapBox>[];
	PrWrapBox?: Partial<PrWrapBox>[];
	SetWrapBox?: Partial<SetWrapBox>[];
}

export interface PartialDeptSceneNotes {
	cmSceneNote?: Partial<CmSceneNote>;
	haSceneNote?: Partial<HaSceneNote>;
	muSceneNote?: Partial<MuSceneNote>;
	prSceneNote?: Partial<PrSceneNote>;
}

export interface PartialDeptEpisodicActorNotes {
	cmEpisodicActor?: Partial<CmEpisodicActor>;
	haEpisodicActor?: Partial<HaEpisodicActor>;
	muEpisodicActor?: Partial<MuEpisodicActor>;
	prEpisodicActor?: Partial<PrEpisodicActor>;
}

export interface DeptChangeLookPictures {
	CmChangePicture?: CmChangePicture[];
	HaLookPicture?: HaLookPicture[];
	MuLookPicture?: MuLookPicture[];
}

export type AnyDeptPermissions =
	| typeof CmPermission
	| typeof HaPermission
	| typeof MuPermission
	| typeof SetPermission
	| typeof PrPermission;
